import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Select } from '@nazka/nazka.mapframe.components'
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { usePublishedAdaptationPlans } from '@services/query.service';
import { useNetworkState } from '@services/hooks.service';
import { getSelectOptionIds } from '@services/utils.service';
import { HAZARD_CATEGORIES } from '@App/components/AdaptationListComponent/adaptation-list-component.constants';
import { useCreateStory } from '@services/mutation.service';
import { setLocationCoordinates, setAddress } from '@library/MaplibreMap/maplibre-map.slice';
import { useOfflineUploadQueue } from '@utils/OfflineUploadQueue/OfflineUploadQueueProvider';
import { trackEvent } from '@services/analytics.service';
import CustomHazardMenu from '@App/components/CustomHazardMenu/CustomHazardMenu'
import ImageUploadField from '@App/components/ImageUploadField/ImageUploadField';
import TranslatedSelect from '@App/components/TranslatedSelect/TranslatedSelect';

import NewLocationMarker from '../MapMarkerLayer/NewLocationMarker';

import './create-story-form.styl'

function CreateStoryForm() {
  const navigate = useNavigate()
  const { t } = useTranslation();
  const { data: adaptationPlans = [] } = usePublishedAdaptationPlans();
  const availablePlans = adaptationPlans.length > 0
    ? adaptationPlans?.map(plan => ({ id: plan.id, label: plan.translations?.en.title })) : [];

  const hazardOptions = useMemo(
    () => HAZARD_CATEGORIES.map(cat => ({ ...cat, label: t(cat.id) })),
    [t],
  );

  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [selectedLinkedPlan, setSelectedLinkedPlan] = useState(null);

  const createStoryMutation = useCreateStory();
  const locationCoordinates = useSelector(state => state.maplibremap.locationCoordinates);
  const address = useSelector(state => state.maplibremap.address);
  const dispatch = useDispatch()
  const isOnline = useNetworkState();
  const { enqueue } = useOfflineUploadQueue();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    resetField,
  } = useForm()

  const normalizeHazardCategoriesForSelect = (hazardCategories) => {
    if (!Array.isArray(hazardCategories)) {
      return [];
    }

    return hazardCategories
      .map((category) => {
        if (!category) {
          return null;
        }

        const categoryId = typeof category === 'string' ? category : category.id;
        if (!categoryId) {
          return null;
        }

        return categoryId;
      })
      .filter(Boolean);
  };

  const handleLinkedPlanSelection = (planId) => {
    const selectedPlan = adaptationPlans.find(plan => plan.id === planId);
    setSelectedLinkedPlan(selectedPlan || null);
  }

  useEffect(() => {
    if (selectedLinkedPlan) {
      resetField('title', { defaultValue: selectedLinkedPlan.translations?.en.title || '' });
      resetField('hazardCategories', {
        defaultValue: normalizeHazardCategoriesForSelect(selectedLinkedPlan.hazardCategories),
      });
      resetField('responsible', { defaultValue: selectedLinkedPlan.responsible || '' });
      dispatch(setAddress(selectedLinkedPlan.address));
      dispatch(setLocationCoordinates({ lat: selectedLinkedPlan.lat, lng: selectedLinkedPlan.lng }));
    }
  }, [resetField, selectedLinkedPlan, dispatch]);

  function onSuccess() {
    navigate('/adaptation/stories');
    dispatch(setLocationCoordinates({ lat: null, lng: null }));
    dispatch(setAddress(null));
  }

  const onSubmit = async (data) => {
    const updatePayload = {
      translations: {
        en: {
          title: data.title,
          description: data.description,
        },
      },
      lat: locationCoordinates.lat,
      lng: locationCoordinates.lng,
      address,
      linkedPlan: data.linkedPlan || null,
      responsible: data.responsible,
      hazardCategories: (data.hazardCategories || [])
        .map(category => (typeof category === 'string' ? category : category?.id))
        .filter(Boolean),
      creatorName: data.name,
    };

    trackEvent('create_story_submitted');

    // If offline, queue for later upload
    if (!isOnline) {
      await enqueue({
        type: 'story',
        payload: {
          ...updatePayload,
          imageFile: imageFile || null,
        },
      });

      toast.info(t('You are offline. Your story has been saved and will be uploaded when you reconnect.'));
      onSuccess();
      return;
    }

    // Online: upload immediately
    const formData = new FormData();
    Object.keys(updatePayload).forEach((key) => {
      if (['translations', 'hazardCategories'].includes(key)) {
        formData.append(key, JSON.stringify(updatePayload[key]));
      } else {
        formData.append(key, updatePayload[key]);
      }
    });

    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    createStoryMutation.mutate(formData, {
      onSuccess,
    });
  };
  return (
    <div className="create-story-form__wrapper">
      <h3>{t('Share your adaptation story')}</h3>

      <div className="create-story-form__field my-eighth-adaptation-step">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <label htmlFor="linkedPlan" className="create-story-form__label">
            {t('Linked plan')}
          </label>
        </div>
        <Controller
          name="linkedPlan"
          control={control}
          render={({ field }) => (
            <Select
              className="create-story-form__select"
              rootElProps={{ id: 'linkedPlan' }}
              value={field.value}
              onInputChange={(value) => {
                field.onChange(value ? value.id : null)
                handleLinkedPlanSelection(value ? value.id : null);
              }}
              options={availablePlans}
              clear
              placeholder={t('Select linked plan...')}
              css={{ borderColor: errors.linkedPlan ? '#dc3545' : '#ddd', color: errors.linkedPlan ? '#dc3545' : '#333' }}
            />
          )}
        />
      </div>

      <div className="create-story-form__image_wrapper">
        <label htmlFor="title" className="create-story-form__label">
          {`${t('Title')} *`}
        </label>
        <input
          id="title"
          type="text"
          {...register('title', {
            required: t('Title is required.'),
            // minLength: { value: 3, message: t('Title must be at least 3 characters.') },
          })}
          className={`create-story-form__input ${errors.title ? 'create-story-form__input--error' : ''
          }`}
          placeholder={t('Enter story title')}
        />
        {errors.title && (
          <p className="create-story-form__error">{errors.title.message}</p>
        )}
      </div>
      <ImageUploadField
        imageError={imageError}
        imageFile={imageFile}
        register={register}
        resetField={resetField}
        setImageError={setImageError}
        setImageFile={setImageFile}
      />
      <div className="create-story-form__address">
        <label htmlFor="address">
          {t('Address')}
          {' '}
          *
        </label>
        {address ? <span>{address}</span> : (
          <span className="create-story-form__address__placeholder">
            {t(isOnline ? 'Search for an address or click on the map to select location.' : 'Address not available offline.')}
          </span>
        )}
      </div>

      <div className="create-story-form__field">
        <label htmlFor="hazardCategories" className="create-story-form__label">
          {`${t('Linked hazard')} *`}
        </label>
        <Controller
          name="hazardCategories"
          control={control}
          rules={{
            required: t('Please select at least one linked hazard.'),
            validate: (value) => {
              if (!value || value.length === 0) {
                return t('Please select at least one linked hazard.');
              }

              return true;
            },
          }}
          render={({ field }) => (
            <TranslatedSelect
              className="create-story-form__select"
              rootElProps={{ id: 'hazardCategories' }}
              value={field.value || []}
              onInputChange={(value) => {
                field.onBlur();
                field.onChange(getSelectOptionIds(value));
              }}
              css={{ borderColor: errors.hazardCategories ? '#dc3545' : '#ddd', color: errors.hazardCategories ? '#dc3545' : '#333' }}
              options={hazardOptions}
              customMenuItems={CustomHazardMenu}
              isMulti
              placeholder={t('Select hazards...')}
            />
          )}
        />
        {errors.hazardCategories && (
          <p className="create-story-form__error">{errors.hazardCategories.message}</p>
        )}
      </div>

      <div className="create-story-form__field">
        <label htmlFor="description" className="create-story-form__label">
          {`${t('What is the adaptation story and how does it address the hazard?')} *`}
        </label>
        <textarea
          id="description"
          rows={4}
          {...register('description', {
            required: t('Description is required.'),
            // minLength: { value: 10, message: t('Description must be at least 10 characters.') },
          })}
          className={`create-story-form__textarea ${errors.description ? 'create-story-form__textarea--error' : ''
          }`}
          placeholder={t('Describe the adaptation story...')}
        />
        {errors.description && (
          <p className="create-story-form__error">{errors.description.message}</p>
        )}
      </div>

      <div className="create-story-form__field">
        <label htmlFor="responsible" className="create-story-form__label">
          {`${t('Who is responsible for this adaptation story?')} *`}
        </label>
        <input
          id="responsible"
          type="text"
          {...register('responsible', {
            required: t('Responsible party is required.'),
            // minLength: { value: 2, message: t('Responsible party must be at least 2 characters.') },
          })}
          className={`create-story-form__input ${errors.responsible ? 'create-story-form__input--error' : ''
          }`}
          placeholder={t('Who is responsible for this story?')}
        />
        {errors.responsible && (
          <p className="create-story-form__error">{errors.responsible.message}</p>
        )}
      </div>

      <div className="create-story-form__image_wrapper">
        <label htmlFor="name" className="create-story-form__label">
          {`${t('Name')} *`}
        </label>
        <input
          placeholder={t('Enter your name')}
          className="create-story-form__input-name"
          {...register('name', {
            required: t('Name is required.'),
          })}
        />
        {errors.name && (
          <span className="create-story-form__error">{errors.name.message}</span>
        )}
      </div>

      <div className="create-story-form__submit__buttons">
        <Button
          css={{
            borderRadius: '10px',
            fontSize: '12px',
            '&:hover': {
              color: 'white',
            },
          }}
          layout="outlined"
          onClick={() => {
            navigate('/adaptation/stories')
            dispatch(setLocationCoordinates({ lat: null, lng: null }))
            dispatch(setAddress(null));
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          css={{
            borderRadius: '10px',
            fontSize: '12px',
            color: 'white',
            '&:hover': {
              color: 'white',
            },
          }}
          loaderProps={{
            css: {
              backgroundColor: 'transparent',
            },
          }}
          disabled={!isValid || isSubmitting || !locationCoordinates.lat || !locationCoordinates.lng}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t(isOnline ? 'Submit' : 'Save offline')}
        </Button>
      </div>
      <NewLocationMarker />
    </div>
  )
}

export default CreateStoryForm
